import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { PlusIcon, SearchIcon, EyeIcon, AlertCircleIcon, ChevronLeftIcon, ChevronRightIcon, RefreshCwIcon, RepeatIcon } from 'lucide-react';
// Storage key for persisting subscription filters
export const SUBSCRIPTION_FILTERS_STORAGE_KEY = 'subscriptionFilters';
// API subscription type
interface ApiSubscription {
  id: string;
  created: number;
  start_date: number;
  next_invoice_date: number;
  number: string;
  name: string;
  cycle: string;
  cycle_num?: number;
  amount: number;
  status: string;
  customer_id: string;
  customer_name: string;
  installments?: number | string;
  passed_installments?: number | string;
}
// Display subscription type
interface Subscription {
  id: string;
  created: Date;
  start_date: Date;
  next_invoice_date: Date;
  number: string;
  name: string;
  cycle: string;
  cycle_num: number;
  amount: number;
  status: string;
  customer_id: string;
  customer_name: string;
  installments: number;
  passed_installments: number;
}
export function Subscriptions() {
  const {
    selectedCompanyId
  } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Data state
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  // Get filter values from URL search params
  const statusFilter = searchParams.get('status') || 'all';
  const searchQuery = searchParams.get('search') || '';
  const pageParam = searchParams.get('page');
  // Local state for search input (for debounce)
  const [searchInput, setSearchInput] = useState(searchQuery);
  // Save current filters to sessionStorage whenever they change
  useEffect(() => {
    const currentFilters = searchParams.toString();
    if (currentFilters) {
      sessionStorage.setItem(SUBSCRIPTION_FILTERS_STORAGE_KEY, currentFilters);
    } else {
      sessionStorage.removeItem(SUBSCRIPTION_FILTERS_STORAGE_KEY);
    }
  }, [searchParams]);
  // Helper function to update URL search params
  const updateSearchParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === 'all') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    // Reset page to 1 when filters change (except when explicitly setting page)
    if (!('page' in updates)) {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };
  // Apply filter from navigation state (from Dashboard clicks)
  useEffect(() => {
    const state = location.state as {
      filterStatus?: string;
    } | null;
    if (state?.filterStatus) {
      updateSearchParams({
        status: state.filterStatus
      });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  // Sync search input with URL param on mount
  useEffect(() => {
    setSearchInput(searchQuery);
  }, []);
  // Fetch subscriptions from API
  const fetchSubscriptions = async (page: number = 1, search: string = '', status: string = 'all') => {
    if (!selectedCompanyId) return;
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {
        company_id: selectedCompanyId,
        page: page
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      if (status !== 'all') {
        params.status = status;
      }
      console.log('Fetching subscriptions with params:', params);
      const response = await callBillingAPI('getSubscriptions', params);
      console.log('Subscriptions API response:', response);
      // Transform API subscriptions
      const apiSubscriptions: ApiSubscription[] = response.items || [];
      const transformedSubscriptions: Subscription[] = apiSubscriptions.map(sub => ({
        id: sub.id,
        created: new Date(sub.created * 1000),
        start_date: new Date(sub.start_date * 1000),
        next_invoice_date: new Date(sub.next_invoice_date * 1000),
        number: sub.number,
        name: sub.name,
        cycle: sub.cycle,
        cycle_num: parseInt(String(sub.cycle_num)) || 1,
        amount: sub.amount,
        status: sub.status,
        customer_id: sub.customer_id,
        customer_name: sub.customer_name,
        installments: parseInt(String(sub.installments)) || 0,
        passed_installments: parseInt(String(sub.passed_installments)) || 0
      }));
      setSubscriptions(transformedSubscriptions);
      setTotalCount(response.total_count || 0);
      setTotalPages(response.total_pages || 0);
      setHasNextPage(response.has_next_page || false);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch subscriptions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load subscriptions');
      setSubscriptions([]);
    } finally {
      setIsLoading(false);
    }
  };
  // Fetch on mount and when company/filters change (from URL params)
  useEffect(() => {
    const page = pageParam ? parseInt(pageParam) : 1;
    fetchSubscriptions(page, searchQuery, statusFilter);
  }, [selectedCompanyId, statusFilter, searchQuery, pageParam]);
  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== searchQuery) {
        updateSearchParams({
          search: searchInput || null
        });
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);
  const handleStatusChange = (newStatus: string) => {
    updateSearchParams({
      status: newStatus
    });
  };
  const handleNextPage = () => {
    if (hasNextPage) {
      updateSearchParams({
        page: String(currentPage + 1)
      });
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 1) {
      updateSearchParams({
        page: currentPage > 2 ? String(currentPage - 1) : null
      });
    }
  };
  const handleRefresh = () => {
    const page = pageParam ? parseInt(pageParam) : 1;
    fetchSubscriptions(page, searchQuery, statusFilter);
  };
  // Check if any filters are active
  const hasActiveFilters = statusFilter !== 'all' || searchQuery;
  // Handle clear filters
  const handleClearFilters = () => {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  };
  // Get cycle display name
  const getCycleName = (cycle: string, cycleNum: number = 1): string => {
    const cycleNames: Record<string, {
      singular: string;
      plural: string;
    }> = {
      week: {
        singular: 'Week',
        plural: 'Weeks'
      },
      month: {
        singular: 'Month',
        plural: 'Months'
      },
      year: {
        singular: 'Year',
        plural: 'Years'
      }
    };
    const cycleName = cycleNames[cycle] || {
      singular: cycle,
      plural: cycle
    };
    if (cycleNum === 1) {
      return `Every ${cycleName.singular}`;
    }
    return `Every ${cycleNum} ${cycleName.plural}`;
  };
  // Get status badge
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, {
      variant: 'success' | 'warning' | 'danger' | 'info' | 'default';
      label: string;
    }> = {
      live: {
        variant: 'success',
        label: 'Live'
      },
      future: {
        variant: 'info',
        label: 'Future'
      },
      expired: {
        variant: 'default',
        label: 'Expired'
      },
      cancelled: {
        variant: 'danger',
        label: 'Cancelled'
      }
    };
    const config = statusConfig[status] || {
      variant: 'default' as const,
      label: status
    };
    return <Badge variant={config.variant} data-id="element-2521">{config.label}</Badge>;
  };
  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const statusOptions = [{
    value: 'all',
    label: 'All Statuses'
  }, {
    value: 'live',
    label: 'Live'
  }, {
    value: 'future',
    label: 'Future'
  }, {
    value: 'expired',
    label: 'Expired'
  }, {
    value: 'cancelled',
    label: 'Cancelled'
  }];
  const columns = [{
    key: 'number',
    header: 'Number',
    render: (sub: Subscription) => <Link to={`/subscriptions/${sub.id}`} className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-2522">
          {sub.number || sub.id.slice(0, 8)}
        </Link>
  }, {
    key: 'name',
    header: 'Name',
    render: (sub: Subscription) => <span className="text-gray-900" data-id="element-2523">{sub.name || '—'}</span>
  }, {
    key: 'customer',
    header: 'Customer',
    render: (sub: Subscription) => <span className="font-medium" data-id="element-2524">{sub.customer_name || 'Unknown'}</span>
  }, {
    key: 'start_date',
    header: 'Start Date',
    render: (sub: Subscription) => <span className="text-gray-600" data-id="element-2525">{formatDate(sub.start_date)}</span>
  }, {
    key: 'cycle',
    header: 'Billing Cycle',
    render: (sub: Subscription) => getCycleName(sub.cycle, sub.cycle_num)
  }, {
    key: 'amount',
    header: 'Amount',
    render: (sub: Subscription) => <span className="font-medium" data-id="element-2526">{formatCurrency(sub.amount)}</span>
  }, {
    key: 'nextBilling',
    header: 'Next Invoice',
    render: (sub: Subscription) => {
      // Don't show next invoice date for cancelled or expired subscriptions
      if (sub.status === 'cancelled' || sub.status === 'expired') {
        return <span className="text-gray-400" data-id="element-2527">—</span>;
      }
      // Don't show next invoice date if all installments are complete
      // (installments is not 0 and passed_installments equals installments)
      if (sub.installments !== 0 && sub.passed_installments === sub.installments) {
        return <span className="text-gray-400" data-id="element-2528">—</span>;
      }
      // Get today's date at midnight for comparison
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const invoiceDate = new Date(sub.next_invoice_date);
      invoiceDate.setHours(0, 0, 0, 0);
      // Only mark as overdue if the date is strictly before today (not including today)
      const isOverdue = sub.status === 'live' && invoiceDate < today;
      return <span className={isOverdue ? 'text-red-600 font-medium' : ''} data-id="element-2529">
            {formatDate(sub.next_invoice_date)}
            {isOverdue && ' (Overdue)'}
          </span>;
    }
  }, {
    key: 'status',
    header: 'Status',
    render: (sub: Subscription) => getStatusBadge(sub.status)
  }, {
    key: 'actions',
    header: '',
    render: (sub: Subscription) => <div className="flex items-center justify-end gap-1" data-id="element-2530">
          <Link to={`/subscriptions/${sub.id}`} data-id="element-2531">
            <Button variant="ghost" size="sm" aria-label="View subscription" data-id="element-2532">
              <EyeIcon className="w-4 h-4" data-id="element-2533" />
            </Button>
          </Link>
        </div>
  }];
  return <div className="space-y-6" data-id="element-2534">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2535">
        <div data-id="element-2536">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2537">Subscriptions</h1>
          <p className="text-gray-500 mt-1" data-id="element-2538">
            {totalCount} subscription{totalCount !== 1 ? 's' : ''} total
          </p>
        </div>
        <div className="flex gap-2" data-id="element-2539">
          <Button variant="secondary" onClick={handleRefresh} disabled={isLoading} leftIcon={<RefreshCwIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} data-id="element-2541" />} data-id="element-2540">
            Refresh
          </Button>
          <Link to="/subscriptions/new" data-id="element-2542">
            <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2544" />} data-id="element-2543">
              New Subscription
            </Button>
          </Link>
          <Button variant="secondary" onClick={() => navigate('/settings', {
          state: {
            openSubscriptionTemplates: true
          }
        })} leftIcon={<RepeatIcon className="w-4 h-4" data-id="element-2546" />} data-id="element-2545">
            New from Template
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card data-id="element-2547">
        <div className="space-y-4" data-id="element-2548">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2549">
            <div className="flex-1 relative" data-id="element-2550">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2551" />
              <Input placeholder="Subscription number or customer" value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-10" data-id="element-2552" />
            </div>
            <div className="w-full sm:w-48" data-id="element-2553">
              <Select options={statusOptions} value={statusFilter} onChange={e => handleStatusChange(e.target.value)} data-id="element-2554" />
            </div>
          </div>

          {hasActiveFilters && <div data-id="element-2555">
              <Button variant="ghost" onClick={handleClearFilters} data-id="element-2556">
                Clear Filters
              </Button>
            </div>}
        </div>
      </Card>

      {/* Content */}
      {isLoading ? <Card data-id="element-2557">
          <div className="p-12 text-center" data-id="element-2558">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2559"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-2560">
              Loading subscriptions...
            </p>
          </div>
        </Card> : error ? <Card data-id="element-2561">
          <div className="p-12 text-center" data-id="element-2562">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-2563">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2564" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2565">
              Failed to load subscriptions
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-2566">{error}</p>
            <Button size="sm" onClick={handleRefresh} data-id="element-2567">
              Retry
            </Button>
          </div>
        </Card> : <>
          <Table columns={columns} data={subscriptions} keyExtractor={sub => sub.id} emptyMessage="No subscriptions found" data-id="element-2568" />

          {/* Pagination */}
          {totalCount > 0 && <Card data-id="element-2569">
              <div className="flex items-center justify-between" data-id="element-2570">
                <p className="text-sm text-gray-600" data-id="element-2571">
                  Page {currentPage} of {totalPages || 1}
                </p>
                <div className="flex gap-2" data-id="element-2572">
                  <Button variant="secondary" size="sm" onClick={handlePrevPage} disabled={currentPage === 1} leftIcon={<ChevronLeftIcon className="w-4 h-4" data-id="element-2574" />} data-id="element-2573">
                    Previous
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleNextPage} disabled={!hasNextPage} rightIcon={<ChevronRightIcon className="w-4 h-4" data-id="element-2576" />} data-id="element-2575">
                    Next
                  </Button>
                </div>
              </div>
            </Card>}
        </>}
    </div>;
}